
import { Signal } from "signals";
import * as PIXI from 'pixi.js';
import Components from "slotworks/components/components";
import ReelComponent from "slotworks/components/matrix/reel/reel-component";
import AbstractReelTransition from "slotworks/components/matrix/reel/transition-behaviours/abstract-reel-transition";
import ReelSpinner from "slotworks/components/matrix/reel/transition-behaviours/spin/reel-spinner";
import SymbolComponent from "slotworks/components/matrix/symbol/symbol-component";
import { AnticipationInfo } from "slotworks/model/gameplay/records/spin-record";
import { SoundEvent } from "slotworks/sound/sound-events";
import SoundManager from "slotworks/sound/sound-manager";
import Contract from "slotworks/utils/contract";
import { parallel, sequence } from "slotworks/utils/race-utils";
import gameState from "slotworks/model/game-state";
import RainbowStacksSpinRecord from "integration/revolver/schema/spin-record";
import RSReelSpinner from "./reel-spinner";
import Layers from "slotworks/graphics/layers/layers";
import * as TweenJS from "slotworks/utils/tween";
import RainbowStacksSpin from "states/spin";
import { ShakeLayers } from "commands/shake-command";
import soundManager from "slotworks/sound/sound-manager";
import canvasManager, { Orientation } from "slotworks/graphics/canvas/canvas-manager";
import graphicsManager from "slotworks/graphics/graphics-manager";
import deviceInfo from "slotworks/utils/device-info";
import { filter } from "lodash";

export default class RSSpinReelTransition extends AbstractReelTransition {

    public onReelLand: Signal = new Signal();

    private spinners: RSReelSpinner[];

    private startGap: number = 100;
    private stopGap: number = 100;
    private quickStopGap: number = 50;

    private allowPartialSkips: boolean = true;

    private anticipation: AnticipationInfo;
    private anticipatingSymbols: SymbolComponent[];

    private spinningReels: number;
    private skipping: boolean;

    private heldReels: number[] = [];

    // Race handler function, see checkRaceComplete
    private completeResolve: Function;

    constructor(startGap: number, stopGap: number, quickStopGap: number, allowPartialSkips: boolean) {
        super();

        this.startGap = startGap;
        this.stopGap = stopGap;
        this.quickStopGap = quickStopGap;
        this.allowPartialSkips = allowPartialSkips;
    }

    public init(reels: ReelComponent[], stops: number[], reelset: string[][]) {
        super.init(reels, stops, reelset);

        this.spinners = [];

        for (let i = 0; i < reels.length; i++) {
            const reel = reels[i];
            const spinner = new RSReelSpinner(reel, stops[i], reelset[i]);
            this.spinners.push(spinner);
            spinner.landSignal.add((index: number) => this.reelLandedHandler(index));
        }
    }

    public start(reelset?: string[][], jumpStart: boolean = false): Contract<void> {
        this.skipping = false;

        SoundManager.event(SoundEvent.reel_transition_start);

        Components.matrix.resetSymbols();

        const startMethods = [];

        for (let spinnerIndex = 0; spinnerIndex < this.spinners.length; spinnerIndex++) {
            if (this.heldReels.indexOf(spinnerIndex) === -1) {
                const spinner = this.spinners[spinnerIndex];
                startMethods.push(() => spinner.start(jumpStart ? 0 : this.startGap * spinnerIndex, jumpStart));
            }
        }

        this.spinningReels = startMethods.length;

        return parallel(startMethods);
    }

    public stop(stops: number[], reelset: string[][], quickSpin: boolean, anticipation: AnticipationInfo): Contract<void> {
        this.anticipatingSymbols = [];
        this.anticipation = anticipation;
        const anticipations = anticipation.reelAnticipations;

        const record = gameState.getCurrentGame().getCurrentRecord() as RainbowStacksSpinRecord;

        const stopMethods: Array<() => Contract<void>> = [];

        let anticipating = false;

        if (anticipations) {
            for (const anticipate of anticipations) {
                if (anticipate) {
                    anticipating = true;
                }
            }
        }

        /* if (quickSpin) {
            anticipating = true;
        }*/

        let haveAnticipated = false;
        for (let spinnerIndex = 0; spinnerIndex < this.spinners.length; spinnerIndex++) {
            if (this.heldReels.indexOf(spinnerIndex) !== -1) {
                continue;
            }

            const spinner = this.spinners[spinnerIndex];

            if (anticipations && anticipations[spinnerIndex]) {
                stopMethods.push(() => spinner.anticipate(stops[spinnerIndex], reelset[spinnerIndex]));
                haveAnticipated = true;
            } else if(haveAnticipated) {
                // stopMethods.push(() => spinner.skip(stops[spinnerIndex], reelset[spinnerIndex]));
                stopMethods.push(() => spinner.stop(stops[spinnerIndex], reelset[spinnerIndex], 0, quickSpin));
            } else {
                stopMethods.push(() => spinner.stop(stops[spinnerIndex], reelset[spinnerIndex], (quickSpin ? this.quickStopGap : this.stopGap) * spinnerIndex, quickSpin));
            }
        }

        if (!anticipating) {
            // If not anticipating, all reels are stopped at the same time (the delay between reels is internal)
            return new Contract<void>((resolve) => {
                parallel(stopMethods).then(() => {
                    SoundManager.event(SoundEvent.reel_transition_end);
                    this.checkRaceComplete(() => resolve(null));
                });
            });
        } else {
            // If anticipation is present, subsequent reels are delayed until the anticipation is finished (since the gap is unpredictable),
            // and then are stopped in parallel until the next anticipation reel and so on
            return new Contract<void>((resolve) => {

                let anticipationStarted = false;

                const getNextStop = (stop: number, parallels: Array<() => Contract<void>>): Contract<any> => {
                    if (stopMethods.length > stop) {
                        if (!anticipations[stop]) {
                            parallels.push(stopMethods[stop]);
                            return getNextStop(stop + 1, parallels);
                        } else {
                            return parallel(parallels).then(() => {
                                if (!anticipationStarted) {
                                    SoundManager.event(SoundEvent.reel_transition_anticipate);
                                    anticipationStarted = true;
                                }
                                stopMethods[stop]().then(() => getNextStop(stop + 1, []));
                            });
                        }
                    } else {
                        if (parallels.length) {
                            SoundManager.customEvent("anticipation_stop");
                            
                            parallel(parallels).then(() => {
                                SoundManager.event(SoundEvent.reel_transition_end);
                                this.checkRaceComplete(() => resolve(null));
                            });
                        } else {
                            SoundManager.event(SoundEvent.reel_transition_end);
                            this.checkRaceComplete(() => resolve(null));
                        }
                    }
                };

                getNextStop(0, []);
            });
        }
    }

    public skip(stops: number[], reelset: string[][]): Contract<void> {
        this.skipping = true;
        let alreadyStopping = false;

        for (const spinner of this.spinners) {
            if (spinner.isStopping()) {
                alreadyStopping = true;
                break;
            }
        }

        if (!alreadyStopping || this.allowPartialSkips) {
            SoundManager.event(SoundEvent.reel_transition_skip);

            const skipMethods: Array<() => Contract<void>> = [];

            for (let i = 0; i < this.spinners.length; i++) {
                skipMethods.push(() => this.spinners[i].skip(stops[i], reelset[i]));
            }

            return new Contract<void>((resolve) => {
                parallel(skipMethods).then(() => {
                    SoundManager.event(SoundEvent.reel_transition_skip_end);
                    this.checkRaceComplete(() => resolve(null));
                });
            });
        } else {
            return new Contract<void>((resolve) => null);
        }
    }

    public jump(stops: number[], reelset: string[][]) {
        for (let i = 0; i < this.spinners.length; i++) {

            const spinner = this.spinners[i];

            spinner.jump(stops[i], reelset[i]);
        }
    }

    public reelLandedHandler(reelIndex: number) {
        this.onReelLand.dispatch(reelIndex);

        const lands = [];
        const landedReel = Components.matrix.getReels()[reelIndex - 1];
        const reelSymbols = landedReel.getSymbols();
        const validLandSymbols = this.anticipation.landSymbols[reelIndex - 1];
        const hasAnticipation = (this.anticipation && this.anticipation.anticipateSymbols.length > reelIndex);
        const symbolsToAnimate = hasAnticipation ? this.anticipation.anticipateSymbols[reelIndex] : [];

        const record = gameState.getCurrentGame().getCurrentRecord() as RainbowStacksSpinRecord;

        if(reelIndex == 4 && record.freespinsTriggered) {
            const symbols = Components.matrix.getGridSymbols().filter(symbol => symbol.symbolId == "SC");
            for(const symbol of symbols) {
                lands.push(() => symbol.land());
            }
            soundManager.customEvent("scatter_land_" + (reelIndex - 1));
        } else {
            for (const symbol of reelSymbols) {
                if (gameState.getCurrentGame().inFreespinsGame && symbol.symbolId == "SC") {
                    lands.push(() => this.scatterLandSpin(symbol));
                } else if (!validLandSymbols || validLandSymbols.indexOf(symbol.symbolId) > -1) {                    
                    lands.push(() => symbol.land());

                    if(symbol.symbolId == "SC") {
                        if(reelIndex == 2) {
                            soundManager.customEvent("scatter_land_1");
                        } else {
                            if(this.anticipation && this.anticipation.reelAnticipations[3]) {
                                soundManager.customEvent("scatter_land_" + (reelIndex - 1));
                            }
                        }                    
                    }

                    if(symbol.symbolId == "J0") {
                        ShakeLayers([Layers.MatrixBackground, Layers.MatrixContent, Layers.MatrixForeground], 70, 6, 4, 6);
                    }

                    // if(symbol.symbolId.includes("J")) {
                    //     symbol.setVisible(false);
                    // }
                }

                if(symbol.symbolId == "J0") {
                    if(reelIndex == 1) {
                        soundManager.customEvent("pot_land_1");
                    } else if(reelIndex == 3 && this.anticipation?.reelAnticipations[4]) {
                        soundManager.customEvent("pot_land_2")
                    }
                } else if(reelIndex == 5 && record.triggersRespin) {
                    soundManager.customEvent("pot_land_3")
                }
            }
        }

        parallel(lands).then(() => {
            this.spinningReels--;
            this.checkRaceComplete();

            if(reelIndex == 4) this.stopAnticipatingScatters();

            if (!this.skipping) {
                if (symbolsToAnimate.length || record.triggersRespin) {
                    let symbols: SymbolComponent[] = [];
                    const reels = Components.matrix.getReels().slice(0, reelIndex);
                    for (const reel of reels) {
                        if (reel.index <= reelIndex) {
                            symbols = symbols.concat(reel.getSymbols());
                        }
                    }
                    for (const symbol of symbols) {
                        if (symbolsToAnimate.indexOf(symbol.symbolId) > -1 || 
                                (record.triggersRespin && symbol.symbolId.includes("J"))) {
                            if (this.anticipatingSymbols.indexOf(symbol) === -1) {
                                symbol.anticipate().execute();
                                symbol.setVisible(false);
                                this.anticipatingSymbols.push(symbol);
                            }
                        }
                    }
                }
            }
        });
    }

    private scatterLandSpin(symbol: SymbolComponent): Contract<void> {
        soundManager.customEvent("fs_scatter_land");

        return parallel([
            () => symbol.win(),
            () => this.scatterText(symbol)
        ]);
    }


    private scatterText(symbol: SymbolComponent) {
        return new Contract(resolve => {
            const fontSize = canvasManager.orientation == Orientation.LANDSCAPE ? 190 : 160;
            const text = new PIXI.BitmapText("+1",
            {
                    fontSize: fontSize,
                    fontName: "silver"
            });
            text.anchor = new PIXI.Point(0.5, 0.5);
            text.name = "sc_text_" + symbol.gridPosition.x + "_" + symbol.gridPosition.y;
            const x = symbol.gridPosition.x;
            const y = symbol.gridPosition.y;
            const id = `symbol_${x}_${y}`;
            const transform = Layers.MatrixContent.localToLocal(
                Layers.Custom.get("Animations"), 
                Layers.MatrixContent.getPosition(id));

            text.position.set(transform.x + transform.width/2 - 10, transform.y + transform.height/2);

            const fsfilter = graphicsManager.createSprite("fsfilter");
            fsfilter.anchor.set(0.5, 0.5);
            fsfilter.position.set(transform.x + transform.width/2, transform.y + transform.height/2);
            fsfilter.name = "sc_filter_" + symbol.gridPosition.x + "_" + symbol.gridPosition.y;

            const scaleMultiplier = canvasManager.orientation == Orientation.LANDSCAPE ? 1 : deviceInfo.widePortrait ? 0.7 : 0.76;
            fsfilter.scale.set(0, 0);
            text.scale.set(0, 0);

            Layers.Custom.get("Animations").add(fsfilter);
            Layers.Custom.get("Animations").add(text);
            new TweenJS.Tween(text.scale)
            .to({x: 1, y: 1}, 400)
            .delay(300)
            .easing(TweenJS.Easing.Back.In)
            .onComplete(resolve)
            .start();

            new TweenJS.Tween(fsfilter.scale)
            .to({x: scaleMultiplier, y: scaleMultiplier}, 400)
            .delay(300)
            .easing(TweenJS.Easing.Back.In)
            .start();

            fsfilter.alpha = 0.1;
            new TweenJS.Tween(fsfilter)
            .to({alpha: 1}, 600)
            .start();
        });
    }

    public hold(reelIndexes?: number[]) {
        if (!reelIndexes) {
            reelIndexes = [];
        }
        this.heldReels = reelIndexes;
    }

    public update() {
        super.update();

        for (const spinner of this.spinners) {
            spinner.update();
        }
    }

    public getSpinners() {
        return this.spinners;
    }

    private stopAnticipatingScatters() {
        for (let i = this.anticipatingSymbols.length - 1; i >= 0; i--) {
            const symbol = this.anticipatingSymbols[i];
            if(symbol.symbolId == "SC") {
                symbol.static();
                this.anticipatingSymbols.splice(i, 1);
            }
        }
    }

    private stopAnticipatingSymbols() {
        const record = gameState.getCurrentGame().getCurrentRecord() as RainbowStacksSpinRecord;

        if(!record.triggersRespin) {
            for (const symbol of this.anticipatingSymbols) {
                symbol.static();
            }
        } else {
            // run pot anticipations to celebrate
            for (const symbol of this.anticipatingSymbols) {
                if(!symbol.symbolId.includes("J")) 
                    symbol.static();
            }
            const symbols = Components.matrix.getGridSymbols();
            for (const symbol of symbols) {
                if (symbol.symbolId.includes("J")) {
                    if (this.anticipatingSymbols.indexOf(symbol) === -1) {
                        symbol.anticipate().execute();
                        symbol.setVisible(false);
                        this.anticipatingSymbols.push(symbol);
                    }
                }
            }
        }

        this.anticipatingSymbols = [];
    }

    // Resolves race condition between symbol land animation and reels stopping
    private checkRaceComplete(resolve?: Function) {
        if (resolve) {
            this.completeResolve = resolve;
        }

        if (this.spinningReels === 0) {
            if (this.completeResolve) {
                this.stopAnticipatingSymbols();
                this.completeResolve();
                this.completeResolve = null;
            }
        }
    }
}

const LANDING_SYMBOLS = ["SC", "J0", "J1", "J2", "J3", "J4"];


// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./src/components/matrix/spin-reel-transition.ts